<template>
  <Fieldset :title="t('activityOccurrences.occurrences')">
    <ActivityScheduleForm v-for="(item, i) in activityOccurrences"
                          :key="i"
                          v-model:activity-id="item.activity.id"
                          v-model:end-date="item.endDate"
                          v-model:id="item.id"
                          v-model:max-participants="item.activity.maxParticipants"
                          v-model:occupied-places="item.occupiedPlaces"
                          v-model:start-date="item.startDate"
                          :index="i"
                          @handle-validation="handleValidation"
                          @register-form-input-ref="addFormInputRef"
                          @remove-activity-occurrence-row="removeActivityOccurrenceRow(i)"/>
    <button
        class="btn btn--border util--ml-auto"
        type="button"
        @click="addActivityOccurrenceRow">
      {{ t("activityOccurrences.add") }}
      <span class="plus-sign"></span>
    </button>
  </Fieldset>
</template>

<script lang="ts" setup>
import {ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import ActivityScheduleForm from "@/components/activities/ActivityScheduleForm.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormInput from "../forms/FormInput.vue";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import {Status} from "@/validation";
import {notifyError, notifySuccess} from "@/notify";
import {Guid} from "@/types";
import {SucceededOrNotResponse} from "@/types/responses";
import {useActivityOccurrenceService} from "@/inversify.config";

const activityOccurrenceService = useActivityOccurrenceService();

// eslint-disable-next-line no-undef
const props = defineProps<{
  activityId?: string,
  modelValue: ActivityOccurrence[]
}>()

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "update:modelValue", value: ActivityOccurrence[]): void;
  (event: "validated", name: string, validationStatus: Status): void;
  (event: "updatedActivityOccurrences", occurrences: ActivityOccurrence[]): void;
}>();

// eslint-disable-next-line
defineExpose({
  //to call validation in parent.
  validateForm
})

const {t} = useI18n();

const formInputs = ref<any[]>([])
const inputValidationStatuses: any = {}

let activityOccurrences = ref<ActivityOccurrence[]>(props.modelValue && props.modelValue.length > 0
    ? props.modelValue
    : [new ActivityOccurrence()])

watch(activityOccurrences, () => validateForm(), {deep: true})
watch(props, (newValue) => {
  activityOccurrences.value = newValue.modelValue;
}, {deep: true, immediate: true})

function addActivityOccurrenceRow() {
  if (!activityOccurrences.value)
    activityOccurrences.value = [];
  activityOccurrences.value.push(new ActivityOccurrence())
}

async function removeActivityOccurrenceRow(index: number) {
  let activityOccurrence = {...activityOccurrences.value[index]};
  if (!activityOccurrence.id?.length) activityOccurrences.value.splice(index, 1)
  else if (await deleteActivityOccurrence(activityOccurrence.id)) {
    activityOccurrences.value.splice(index, 1)
  }
}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref) && ref)
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function validateForm() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())

  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    emit("validated", "", {valid: false});
    notifyError(t('activityOccurrences.form.mustChooseDate'))
    return
  }

  const hasDuplicates = validateDuplicateActivityScheduleDates();
  if (hasDuplicates) {
    emit("validated", "", {valid: false});
    notifyError(t('activityOccurrences.form.saveDuplicateError'))
    return
  }

  emit("update:modelValue", activityOccurrences.value);
  emit("validated", "activityOccurenceForm", {valid: true});
}

function validateDuplicateActivityScheduleDates() {
  if (!activityOccurrences.value)
    return false;

  activityOccurrences.value.forEach((activity: ActivityOccurrence) => {
    if (!activity.endDate) activity.endDate = undefined
  });

  return activityOccurrences.value.filter((activity: ActivityOccurrence, index: number) =>
      activityOccurrences.value.some((otherActivity: ActivityOccurrence, otherIndex: number) =>
          index !== otherIndex &&
          activity.startDate?.split("T")[0] === otherActivity.startDate?.split("T")[0] &&
          activity.endDate?.split("T")[0] === otherActivity.endDate?.split("T")[0]
      )
  ).length > 0;
}

async function deleteActivityOccurrence(id?: string): Promise<boolean> {
  if (!Guid.isValid(id ?? "") || id == null) return false;

  let confirmDelete = confirm(t("activityOccurrences.form.deleteConfirmation"));
  if (!confirmDelete)
    return false;

  let result: SucceededOrNotResponse = await activityOccurrenceService.deleteActivityOccurrence(id);
  if (!result?.succeeded) {
    notifyError(t('activityOccurrences.form.deleteError'))
    return false
  }
  notifySuccess(t('activityOccurrences.form.deleteSuccess'))
  emit("updatedActivityOccurrences", activityOccurrences.value);
  return true;
}
</script>